/**
 * One organism's screen (§A.2 layout): brain | market view | live log on
 * top, portfolio/equity | life | family underneath, all driven by one
 * shared `PlaybackStore` so the log's seq scrubber drives the brain (and
 * vice versa — requirement 4).
 *
 * Owner directive 2026-09-27: "the layout was still not improved ... its
 * currently cramped, the live log especially." This is the brain-dominant,
 * three-zone layout DESIGN.md §1/§8 describes: a main column (brain beside
 * market, portfolio full width, a life/family summary strip) next to a
 * full-height, resizable live-log rail — collapsing to a bottom dock
 * between 1100-1499px and a single stacked column below that
 * (`src/app/layoutState.ts`'s `resolveLayoutZone`). Panel collapse state,
 * the rail width, and the dock's expanded/collapsed state all persist to
 * `localStorage` (`layoutState.ts`) so a reader's layout survives a reload.
 */

import type { RoleLocation, RoleSnapshot } from "../data/snapshotIndex";
import { getRoleSnapshot } from "../data/RoleSnapshotRegistry";
import { resolveAllRoleLocations } from "../data/roleDirectory";
import { prefetchOtherOrganisms } from "../data/backgroundPrefetch";
import { renderNotPublishedYet } from "../panels/NotPublishedYet";
// Type-only: the brain panel pulls in three.js and its own region/tooltip/
// scene submodules (§B.6's budgeted-first-paint concern) — a sizeable
// chunk of the bundle that the hero/feed above never needs. `mountPanels`
// below `await import()`s the real module so Rollup code-splits the whole
// `brain/*` + `three` subtree into its own chunk, fetched only once this
// screen actually mounts it (2026-09-28 bundle-size fix).
import type { BrainPanel } from "../brain/BrainPanel";
import { LogPanel } from "../log/LogPanel";
import { PortfolioPanel } from "../panels/PortfolioPanel";
import { LifePanel } from "../panels/LifePanel";
import { MarketPanel } from "../panels/MarketPanel";
import { SizingPanel } from "../panels/SizingPanel";
import { BenchmarksPanel } from "../panels/BenchmarksPanel";
import { VitalsHero } from "../panels/VitalsHero";
import { config } from "../config";
import { getPlaybackStore } from "../state/PlaybackStoreRegistry";
import { attachTooltip } from "../ui/tooltip";
import {
  type LayoutState,
  clampRailWidth,
  loadLayoutState,
  resolveLayoutZone,
  saveLayoutState,
} from "./layoutState";

/** Per-panel explanations for the header tooltip (owner requirement
 * 2026-09-27: "every panel header ... has a short tooltip"). Kept here,
 * next to the one place every panel header is built, rather than scattered
 * per-panel-module, so the whole screen's header copy is reviewable in one
 * place. */
const PANEL_TOOLTIPS: Readonly<Record<string, string>> = {
  Brain:
    "Neuron activity for this organism's compiled connectome — hover a point for its region, class, and current activity.",
  "Market view":
    "The organism's attended symbols: last price, bid/ask spread, session state, and staleness, folded from market.view events.",
  "Live log":
    "Every catalog event for this organism, newest first — filter by kind, click a row to scrub the brain/market to that moment.",
  "Portfolio + equity":
    "NAV, cash, positions, and P&L from this organism's own ledger, plus its equity curve over time.",
  Benchmarks:
    "Live NAV since the episode's genesis vs equal-weight buy-and-hold, the 60% core-only reference, monthly buy-and-hold and cash — key stats, regimes, and the rolling 30-day gate.",
  "Positions & sizing":
    "Per-symbol target vs marked weight against the caps, raw vs smoothed signal, cooldown status, and the last sizing decision in plain words.",
  Life: "Projected life expectancy from the natural (regime) and economic (solvency) hazard models.",
  Family:
    "Procreation readiness and children — see the fleet-wide Family screen for the full tree.",
};

/** Grid-area keys (`data-panel`, `src/style.css`'s `.obs-organism-shell`)
 * per panel title — a separate, stable identifier from the display title so
 * CSS never has to select on human-readable text. */
const PANEL_KEYS: Readonly<Record<string, string>> = {
  Brain: "brain",
  "Market view": "market",
  "Live log": "log",
  "Portfolio + equity": "portfolio",
  Benchmarks: "benchmarks",
  "Positions & sizing": "sizing",
  Life: "life",
  Family: "family",
};

interface Disposable {
  dispose(): void;
}

export class OrganismScreen {
  private disposables: Disposable[] = [];
  private readonly playback: ReturnType<typeof getPlaybackStore>;
  private brain: BrainPanel | null = null;
  private life: LifePanel | null = null;
  private role: RoleSnapshot | null = null;
  /** Set by `dispose()` — guards `mountPanels`' brain-panel construction
   * against the dynamically-imported chunk resolving after this screen has
   * already been torn down (code-review finding). */
  private disposed = false;

  private shellEl: HTMLDivElement | null = null;
  private layoutState: LayoutState = loadLayoutState();
  private resizeHandler: (() => void) | null = null;
  private dragCleanup: (() => void) | null = null;

  /** Exposed for the Playwright smoke test only (via `window.__obsApp`) —
   * lets it reach `BrainPanel.pickAt`/`pointCount`/`wakeCount` without a
   * real mouse event against a headless GPU (requirement 6). Never read by
   * any panel itself. */
  get brainPanel(): BrainPanel | null {
    return this.brain;
  }

  /** Exposed for the Playwright smoke test only — lets it await
   * `role.events()` growing after mutating the on-disk fixture mid-test
   * (the owner's caching-evidence requirement: "a simulated new chunk
   * appears within one poll"), instead of guessing at a UI selector to
   * poll. Never read by any panel itself. */
  get roleSnapshot(): RoleSnapshot | null {
    return this.role;
  }

  constructor(
    private readonly container: HTMLElement,
    private readonly location: RoleLocation,
  ) {
    this.playback = getPlaybackStore(this.location.role);
  }

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    // Kicked off first, before even the role snapshot resolves, so the
    // brain chunk's (three.js + its scene/region/tooltip submodules)
    // network fetch overlaps the role-snapshot round trip instead of
    // starting only after it — this import doesn't depend on the resolved
    // role at all (code-review finding: a prior version started it inside
    // `mountPanels`, which only runs once `getRoleSnapshot` has already
    // fully resolved, serializing the two fetches for no reason).
    const brainModulePromise = import("../brain/BrainPanel");
    // Observed unconditionally, right away (code-review finding): a
    // `.catch()` attached only inside the `if (!role)` branch below never
    // runs when `getRoleSnapshot` itself *rejects* (a real error, not the
    // "never published" case, which it returns `null` for instead of
    // throwing) — execution never reaches that branch, so the import's own
    // rejection (if it also fails, e.g. a stale-deploy chunk 404) would be
    // truly unhandled alongside the propagated error. This no-op catch only
    // marks the promise "observed" for that warning; the real value/error
    // is still awaited properly by `mountPanels`' `mountBrain` below on the
    // success path.
    brainModulePromise.catch(() => {});
    const role = await getRoleSnapshot(this.location);
    this.role = role;
    if (!role) {
      this.container.appendChild(
        renderNotPublishedYet(
          this.location.displayName,
          "This organism has never published a snapshot (no latest.json at its snapshot path yet).",
        ),
      );
      return;
    }

    this.layoutState = loadLayoutState();

    // The vitals hero (design/revamp/DIRECTION.md §1.1) — sits above the
    // panel grid, its own small live poller, disposed alongside every other
    // panel below.
    const heroEl = document.createElement("div");
    this.container.appendChild(heroEl);
    const hero = new VitalsHero(heroEl, role);
    this.disposables.push(hero);
    // Fire-and-forget: the hero is a derived, secondary view of data the
    // panels below fetch independently on their own critical path — it must
    // never block first paint of Brain/Market/Log, and a real fetch failure
    // here is not fatal to the rest of the screen (caught, not thrown).
    this.mountResilient("vitals hero", () => hero.mount()).catch((error: unknown) => {
      console.warn("observatory: vitals hero failed to mount", error);
    });

    const shell = document.createElement("div");
    shell.className = "obs-organism-shell";
    this.shellEl = shell;

    const brainPanelEl = this.makePanel(shell, "Brain");
    const marketPanelEl = this.makePanel(shell, "Market view");

    const handle = document.createElement("div");
    handle.className = "obs-log-rail-handle";
    attachTooltip(handle, "Drag to resize the live log rail.");
    shell.appendChild(handle);

    const logPanelEl = this.makePanel(shell, "Live log");
    const dockToggle = document.createElement("button");
    dockToggle.className = "obs-log-dock-toggle";
    dockToggle.textContent = this.layoutState.logDockExpanded ? "▾ dock" : "▸ dock";
    attachTooltip(
      dockToggle,
      "Expand or collapse the log dock (only shown when the log docks to the bottom of the page).",
    );
    dockToggle.addEventListener("click", () => {
      this.layoutState = {
        ...this.layoutState,
        logDockExpanded: !this.layoutState.logDockExpanded,
      };
      saveLayoutState(this.layoutState);
      this.applyZone();
      dockToggle.textContent = this.layoutState.logDockExpanded ? "▾ dock" : "▸ dock";
    });
    logPanelEl.header.appendChild(dockToggle);

    const portfolioEl = this.makePanel(shell, "Portfolio + equity");
    const benchmarksEl = this.makePanel(shell, "Benchmarks");
    const sizingEl = this.makePanel(shell, "Positions & sizing");
    const lifeEl = this.makePanel(shell, "Life");
    const familyEl = this.makePanel(shell, "Family");
    familyEl.body.appendChild(
      renderNotPublishedYet(
        "Family",
        "See the fleet-wide Family screen (nav bar above) for readiness, tree, and children.",
      ),
    );

    this.container.appendChild(shell);
    this.applyZone();
    this.wireResize();
    this.wireDragHandle(handle);

    await this.mountPanels(
      role,
      brainModulePromise,
      brainPanelEl.body,
      marketPanelEl.body,
      logPanelEl.body,
      portfolioEl.body,
      benchmarksEl.body,
      sizingEl.body,
      lifeEl.body,
    );

    // Family never publishes anything at the per-organism level (it's a
    // pointer to the fleet-wide Family screen) — auto-collapse it to a slim
    // strip by default (DESIGN.md §7), unless the reader already chose to
    // expand it this session.
    this.applyAutoCollapse(familyEl.root, "Family", true);
    // Life auto-collapses only when its projection is not yet substantive
    // (still shows the real, if mostly-dashes, table on demand).
    this.applyAutoCollapse(lifeEl.root, "Life", this.life ? !this.life.substantive : false);

    // Owner requirement 4 ("background loading with priorities via
    // requestIdleCallback"): this screen's own panels above always load
    // first, eagerly — only once they have does this warm the *other*
    // known organisms' RoleSnapshotRegistry entries during idle time, so
    // switching to them later needs no network round trip.
    const allLocations = await resolveAllRoleLocations();
    prefetchOtherOrganisms(allLocations, this.location);
  }

  private async mountPanels(
    role: RoleSnapshot,
    brainModulePromise: Promise<typeof import("../brain/BrainPanel")>,
    brainBody: HTMLElement,
    marketBody: HTMLElement,
    logBody: HTMLElement,
    portfolioBody: HTMLElement,
    benchmarksBody: HTMLElement,
    sizingBody: HTMLElement,
    lifeBody: HTMLElement,
  ): Promise<void> {
    const market = new MarketPanel(marketBody, role);
    const log = new LogPanel(logBody, role, this.playback);
    const portfolio = new PortfolioPanel(portfolioBody, role);
    const benchmarks = new BenchmarksPanel(benchmarksBody, role);
    const sizing = new SizingPanel(sizingBody, role);
    const life = new LifePanel(lifeBody, role);
    this.life = life;
    this.disposables.push(market, log, portfolio, benchmarks, sizing, life);

    const mountBrain = async (): Promise<void> => {
      const { BrainPanel } = await brainModulePromise;
      // Torn down (App.showRoute disposes the outgoing screen synchronously
      // on every navigation, regardless of whether this screen's own
      // mount() has resolved) while the chunk was in flight — never
      // construct a brain panel nothing will ever dispose (code-review
      // finding, same class of race PortfolioPanel's chart import guards
      // against).
      if (this.disposed) return;
      const brain = new BrainPanel(brainBody, role, this.playback);
      this.brain = brain;
      this.disposables.push(brain);
      try {
        await brain.mount();
      } catch (error) {
        brain.dispose(); // a retry (mountResilient) builds a fresh panel
        throw error;
      }
    };

    await Promise.all([
      this.mountResilient("brain", mountBrain),
      this.mountResilient("market", () => market.mount()),
      this.mountResilient("log", () => log.mount()),
      this.mountResilient("portfolio", () => portfolio.mount()),
      this.mountResilient("benchmarks", () => benchmarks.mount()),
      this.mountResilient("sizing", () => sizing.mount()),
      this.mountResilient("life", () => life.mount()),
    ]);
  }

  /** OQA: a panel whose FIRST fetch fails transiently (a timeout on a congested
   * link, an object 404 in the publish window) used to reject `mount()` before its
   * poller started — that panel then stayed blank for the rest of the visit (seen
   * on the live female page: the vitals hero never rendered after one timeout).
   * Retries the mount on the poll cadence until it succeeds or the screen is
   * disposed. Every panel starts its poller only after its first fetch, so a
   * failed attempt has nothing to tear down. */
  private async mountResilient(name: string, mount: () => Promise<void>): Promise<void> {
    for (let attempt = 1; ; attempt += 1) {
      try {
        await mount();
        return;
      } catch (error) {
        if (this.disposed) return;
        console.warn(`observatory: ${name} panel failed to mount (attempt ${attempt})`, error);
        await new Promise((resolve) => window.setTimeout(resolve, config.pollIntervalMs));
        if (this.disposed) return;
      }
    }
  }

  /** Applies (or reapplies, on window resize) the current breakpoint zone
   * and the persisted rail width / dock height to the shell — a handful of
   * CSS custom properties + one class name; the actual grid-area layout per
   * zone lives entirely in `style.css`, not here. */
  private applyZone(): void {
    if (!this.shellEl) return;
    const width = window.innerWidth;
    const zone = resolveLayoutZone(width);
    this.shellEl.classList.remove("obs-zone-three", "obs-zone-two", "obs-zone-single");
    this.shellEl.classList.add(
      zone === "three-zone"
        ? "obs-zone-three"
        : zone === "two-zone"
          ? "obs-zone-two"
          : "obs-zone-single",
    );

    const railPx = clampRailWidth(this.layoutState.railWidthPx, width);
    this.shellEl.style.setProperty("--obs-rail-width", `${railPx}px`);
    this.shellEl.style.setProperty(
      "--obs-log-dock-height",
      this.layoutState.logDockExpanded ? "34vh" : "40px",
    );
  }

  private wireResize(): void {
    let scheduled = false;
    this.resizeHandler = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        this.applyZone();
      });
    };
    window.addEventListener("resize", this.resizeHandler);
  }

  /** Drag-to-resize for the log rail (DESIGN.md §8), only meaningful in the
   * three-zone layout (CSS hides the handle otherwise). The rail's width is
   * "distance from the shell's right edge to the pointer," since the log
   * rail is always the shell's last, right-aligned column. */
  private wireDragHandle(handle: HTMLElement): void {
    let dragging = false;

    const onMove = (event: MouseEvent): void => {
      if (!dragging || !this.shellEl) return;
      const rect = this.shellEl.getBoundingClientRect();
      const width = clampRailWidth(rect.right - event.clientX, window.innerWidth);
      this.shellEl.style.setProperty("--obs-rail-width", `${width}px`);
    };

    const onUp = (): void => {
      if (!dragging) return;
      dragging = false;
      document.body.classList.remove("obs-resizing-rail");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const current = Number.parseInt(
        this.shellEl?.style.getPropertyValue("--obs-rail-width") ?? "",
        10,
      );
      if (Number.isFinite(current)) {
        this.layoutState = { ...this.layoutState, railWidthPx: current };
        saveLayoutState(this.layoutState);
      }
    };

    const onDown = (event: MouseEvent): void => {
      event.preventDefault();
      dragging = true;
      document.body.classList.add("obs-resizing-rail");
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };

    handle.addEventListener("mousedown", onDown);
    this.dragCleanup = () => {
      handle.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }

  /** Default-collapses a panel to its slim header-only strip (DESIGN.md
   * §7) — but only when the reader hasn't already made an explicit choice
   * for this panel this session (`layoutState.collapsedPanels` records real
   * clicks on the chevron, never this default). */
  private applyAutoCollapse(
    root: HTMLDivElement,
    title: string,
    shouldCollapseByDefault: boolean,
  ): void {
    if (title in this.layoutState.collapsedPanels) return; // explicit user choice wins
    root.classList.toggle("obs-panel-collapsed", shouldCollapseByDefault);
  }

  /** One panel's chrome: a collapsible header (title + tooltip explaining
   * what the panel shows) and a body slot. Every organism-screen panel goes
   * through this one place (declutter requirement: "consistent header,
   * collapsible panels with clear titles"). Collapse state persists to
   * `localStorage` per panel title (`layoutState.ts`). */
  private makePanel(
    parent: HTMLElement,
    title: string,
  ): { root: HTMLDivElement; header: HTMLDivElement; body: HTMLDivElement } {
    const root = document.createElement("div");
    root.className = "obs-panel";
    root.dataset["panel"] = PANEL_KEYS[title] ?? title.toLowerCase();
    const persisted = this.layoutState.collapsedPanels[title];
    if (persisted) root.classList.add("obs-panel-collapsed");

    const header = document.createElement("div");
    header.className = "obs-panel-header";

    const collapse = document.createElement("button");
    collapse.className = "obs-panel-collapse";
    collapse.textContent = "▾";
    attachTooltip(collapse, `Collapse or expand the ${title} panel.`);
    collapse.addEventListener("click", () => {
      const collapsed = root.classList.toggle("obs-panel-collapsed");
      this.layoutState = {
        ...this.layoutState,
        collapsedPanels: { ...this.layoutState.collapsedPanels, [title]: collapsed },
      };
      saveLayoutState(this.layoutState);
    });

    const titleEl = document.createElement("span");
    titleEl.className = "obs-panel-title";
    titleEl.textContent = title;
    const tip = PANEL_TOOLTIPS[title];
    if (tip) attachTooltip(titleEl, tip);

    header.appendChild(collapse);
    header.appendChild(titleEl);

    const body = document.createElement("div");
    body.className = "obs-panel-body";
    root.appendChild(header);
    root.appendChild(body);
    parent.appendChild(root);
    return { root, header, body };
  }

  dispose(): void {
    this.disposed = true;
    for (const disposable of this.disposables) disposable.dispose();
    this.disposables = [];
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    this.dragCleanup?.();
  }
}
